import { useEffect, useMemo, useState } from 'react';
import { getStorage } from '@/store/storage';
import { previewRevision } from '@/constants/utils/html_interaction';
const pendingWrites = new Map();

export default function useHtmlPreviewHistory(html, context) {
  const current = useMemo(() => previewRevision(html), [html]);
  const scope = JSON.stringify([context?.userId, context?.conversationId, context?.messageId]);
  const enabled = context?.canPersist && context?.userId && context?.conversationId && context?.messageId;
  const [state, setState] = useState({ scope, current, versions: [], selected: null });
  useEffect(() => {
    let alive = true;
    setState({ scope, current, versions: [], selected: null });
    if (!enabled || html.length > 200000) return undefined;
    const key = `gab-html-history-v1:${scope}`;
    const task = (pendingWrites.get(key) || Promise.resolve()).catch(() => {}).then(async () => {
      if (!alive) return;
      let older = [];
      try {
        const raw = await getStorage().getItem(key);
        const parsed = raw && raw.length <= 650000 ? JSON.parse(raw) : [];
        if (Array.isArray(parsed)) older = parsed.filter(v => v && typeof v.html === 'string' && v.html.length <= 200000 && v.id === previewRevision(v.html) && v.id !== current).slice(0, 2);
      } catch { /* History is optional; the current source always remains usable. */ }
      if (!alive) return;
      const versions = [{ id: current, html }, ...older];
      setState({ scope, current, versions, selected: null });
      try { await getStorage().setItem(key, JSON.stringify(versions)); } catch { /* Keep available revisions in memory. */ }
    });
    pendingWrites.set(key, task);
    task.finally(() => { if (pendingWrites.get(key) === task) pendingWrites.delete(key); });
    return () => { alive = false; };
  }, [scope, current, enabled]);
  const versions = state.scope === scope && state.current === current ? state.versions : [];
  const selected = versions.find(v => v.id === state.selected);
  return { html: selected?.html || html, versions, selectedId: selected?.id || current, select: id => setState(v => ({ ...v, selected: id })) };
}
